import {CUSTOM_NOTIFICATIONS} from './consts';
import {global as Bus} from './EventBus';


/**
 * @typedef CustomFont
 * @property {string} name The name of the font
 * @property {string} address The URL of the font file
 * @property {string} weight
 * @property {string} family
 */

export const FontFamilyList = [
  'Almaden Sans',
  'Arial',
  'Arial Black',
  'Verdana',
  'Tahoma',
  'Trebuchet MS',
  'Impact',
  'Times New Roman',
  'Georgia',
  'Courier New',
  'Comic Sans MS',
];

/**
 *
 * @param {{fontList: CustomFont[]} | null} response
 */
export const alterFontListResp = (response) => {
  const fonts = response?.fontList ?? [];
  if (fonts.some((font) => font.name.match(/fidelity/i))) {
    try {
      const url = new URL(window.location.href);
      // If customer is using vanity URL, load from origin
      const origin = `https://${url.hostname}`;
      response.fontList = [
        {
          'address': `${origin}/zbook/fonts/FidelitySans-Regular.woff`,
          'family': 'Fidelity Sans',
          'name': 'Fidelity Sans Regular',
          'weight': '400',
        },
        {
          'address': `${origin}/zbook/fonts/FidelitySans-Bold.woff`,
          'family': 'Fidelity Sans',
          'name': 'Fidelity Sans Bold',
          'weight': '700',
        },
      ];
    } catch {}
  }
};

/**
 * Populate newly added family, weight fields for backwards compat:
 *   - No weight falls back to 400
 *   - No font family falls back to the font 'name'
 * @param {*} fontList
 * @return {CustomFont}
 */
export const normalizeFontList = (fontList) => {
  const fontSpecs = fontList ?? [];
  return fontSpecs.map((fontSpec) => {
    return ({
      name: fontSpec.name,
      family: fontSpec.family ?? fontSpec.name,
      weight: fontSpec.weight ?? '400',
      address: fontSpec.address,
    });
  });
};

/**
 * Given a flat list of font specs, collect multiple fonts of the same family into representatives
 * The representative should be the font of that family with weight closest to 400.
 * @param {CustomFont[]} fontList
 * @return {CustomFont[]}
 */
export const collectFontFamilyRepresentatives = (fontList) => {
  const fontDict = {};
  for (const font of fontList) {
    if (fontDict[font.family]) {
      const currFontRep = fontDict[font.family];
      const currWt = parseFloat(currFontRep.weight);
      const fontWt = parseFloat(font.weight);
      if (Math.abs(currWt - 400) > Math.abs(fontWt - 400)) {
        fontDict[font.family] = font;
      }
    } else {
      fontDict[font.family] = font;
    }
  }
  return Array.from(Object.values(fontDict));
};

/**
 * Firefox has a bug when using Array.from to convert FontFaceSet.values() to an array,
 *  manually create the array from the iterator
 * @param {Iterator} iterable
 * @return {Array}
 */
export const fontFaceSetIteratorToArray = (iterable) => {
  const results = [];
  let iterator = iterable.next();
  while (iterator.done === false) {
    results.push(iterator.value);
    iterator = iterable.next();
  }
  return results;
};

/**
 * @param {CustomFont[]} fontFaceObjs
 * @return {Promise<FontFace>[]}
 */
export const loadFontFaces = (fontFaceObjs) => {
  return fontFaceObjs.map((fontSpec) => loadCustomFont(fontSpec));
};

/**
 * Load a custom font from a URL
 * @param {CustomFont} fontSpec
 * @return {Promise<{fontName: string, fontFace: FontFace}|null>}
 */
export const loadCustomFont = async (fontSpec) => {
  try {
    const fontName = fontSpec.family ?? fontSpec.name;
    if (!fontSpec.address || !fontName) {
      throw new Error('Invalid font URL or font name');
    }

    const regex = /\.(woff2?|WOFF2?)(\?.*)?$/i; // Only matches woff or woff2, other font files are not supported yet
    const match = fontSpec.address.match(regex);

    const type = match ? match[1].toLowerCase() : null;

    if (!type) {
      throw new Error('File type error, not a WOFF/WOFF2 font file.');
    }

    // Attempt to load the font via font-src:
    const fontFace = new FontFace(fontName, `url('${fontSpec.address}')`, {weight: fontSpec.weight ?? '400'});
    const MAX_WAIT = 10000;
    // If font takes longer than MAX_WAIT to load, reject the font
    await Promise.race([
      fontFace.load(), // Throws NetworkError if load fails
      new Promise((_, reject) =>
        setTimeout(() => {
          reject(new Error('Font file took too long'));
        }, MAX_WAIT)
      ),
    ]);
    document.fonts.add(fontFace);
    Bus.emit(CUSTOM_NOTIFICATIONS.onCustomFontLoaded, fontSpec);

    console.log(`Font '${fontSpec.name}' applied successfully!`);
    return {
      fontName,
      fontFace: fontFace,
    };
  } catch (error) {
    console.error('Error loading font:', error);
    return null;
  }
};
